import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/** Velouré cart drawer: native Horizon updates + landing-page return + overlay page lock. */
class CartDrawerComponent extends Component {
  #summaryThreshold = 0.5;
  #scrollY = 0;
  #pageLocked = false;

  /** @type {import('@shopify/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@shopify/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    this.#rememberLandingUrl();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    this.#themeDrawer?.addEventListener(
      DrawerCloseEvent.eventName,
      this.#handleDrawerClose
    );

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    this.#themeDrawer?.removeEventListener(
      DrawerCloseEvent.eventName,
      this.#handleDrawerClose
    );

    this.#unlockPage();
  }

  #rememberLandingUrl() {
    const landing = /** @type {HTMLElement | null} */ (
      document.querySelector('[data-veloure-landing-url]')
    );

    const relativeUrl = landing?.dataset.veloureLandingUrl;

    if (!relativeUrl) return;

    try {
      sessionStorage.setItem(
        'veloureLandingUrl',
        new URL(relativeUrl, window.location.origin).href
      );
    } catch (_) {
      sessionStorage.setItem('veloureLandingUrl', relativeUrl);
    }
  }

  #getLandingUrl() {
    this.#rememberLandingUrl();

    return (
      sessionStorage.getItem('veloureLandingUrl') ||
      window.location.origin
    );
  }

  #updateContinueShoppingLink() {
    const target = /** @type {HTMLElement | null} */ (
      this.querySelector('.cart-items__empty-button')
    );

    if (!target) return;

    const landingUrl = this.#getLandingUrl();

    if (target instanceof HTMLAnchorElement) {
      target.href = landingUrl;
    }

    target.textContent = 'Retourner à l’offre';
    target.dataset.veloureLandingUrl = landingUrl;

    if (target.dataset.veloureReturnBound === 'true') return;

    target.dataset.veloureReturnBound = 'true';

    target.addEventListener('click', (event) => {
      event.preventDefault();

      window.location.assign(
        target.dataset.veloureLandingUrl || landingUrl
      );
    });
  }

  #lockPage() {
    document.documentElement.classList.add('vel-cart-open');
    document.body.classList.add('vel-cart-open');

    if (
      window.matchMedia('(max-width: 989px)').matches ||
      this.#pageLocked
    ) {
      return;
    }

    this.#scrollY = window.scrollY;

    document.body.style.position = 'fixed';
    document.body.style.inset = `-${this.#scrollY}px 0 0 0`;
    document.body.style.width = '100%';

    this.#pageLocked = true;
  }

  #unlockPage() {
    document.documentElement.classList.remove('vel-cart-open');
    document.body.classList.remove('vel-cart-open');

    if (!this.#pageLocked) return;

    document.body.style.position = '';
    document.body.style.inset = '';
    document.body.style.width = '';

    window.scrollTo(0, this.#scrollY);

    this.#pageLocked = false;
  }

  #parseDisplayedMoney(value) {
    const raw = String(value || '')
      .replace(/\s/g, '')
      .replace(/[^0-9,.-]/g, '');

    if (!raw) return Number.NaN;

    const lastComma = raw.lastIndexOf(',');
    const lastDot = raw.lastIndexOf('.');
    const decimalIndex = Math.max(lastComma, lastDot);

    if (decimalIndex < 0) {
      return Number(raw.replace(/[^0-9-]/g, ''));
    }

    const integerPart = raw
      .slice(0, decimalIndex)
      .replace(/[.,]/g, '');

    const decimalPart = raw
      .slice(decimalIndex + 1)
      .replace(/[.,]/g, '');

    return Number(`${integerPart}.${decimalPart}`);
  }

  #updateDiscountBadges() {
    const rows = this.querySelectorAll(
      '.cart-items__table-row:not(.cart-items__nested-line)'
    );

    rows.forEach((row) => {
      const priceWrapper = row.querySelector(
        '.cart-items__unit-price-wrapper'
      );

      const compareElement = priceWrapper?.querySelector(
        's.compare-at-price, .compare-at-price'
      );

      const currentElement = priceWrapper?.querySelector(
        'span:not(.visually-hidden)'
      );

      const existingBadge = priceWrapper?.querySelector(
        '.vel-line-discount-badge'
      );

      if (
        !priceWrapper ||
        !compareElement ||
        !currentElement
      ) {
        existingBadge?.remove();
        return;
      }

      const comparePrice = this.#parseDisplayedMoney(
        compareElement.textContent
      );

      const currentPrice = this.#parseDisplayedMoney(
        currentElement.textContent
      );

      if (
        !Number.isFinite(comparePrice) ||
        !Number.isFinite(currentPrice) ||
        comparePrice <= currentPrice ||
        comparePrice <= 0
      ) {
        existingBadge?.remove();
        return;
      }

      const percentage = Math.round(
        ((comparePrice - currentPrice) / comparePrice) * 100
      );

      if (percentage <= 0) {
        existingBadge?.remove();
        return;
      }

      const badge =
        existingBadge || document.createElement('span');

      badge.className = 'vel-line-discount-badge';
      badge.textContent = `-${percentage} %`;

      badge.setAttribute(
        'aria-label',
        `Réduction de ${percentage} pour cent`
      );

      if (!existingBadge) {
        priceWrapper.querySelector('div')?.append(badge);
      }
    });
  }

  #handleDrawerOpen = () => {
    this.#lockPage();
    this.#updateStickyState();
    this.#updateContinueShoppingLink();
    this.#updateDiscountBadges();

    customElements
      .whenDefined('shopify-payment-terms')
      .then(() => {
        const cta = this
          .querySelector('shopify-payment-terms')
          ?.shadowRoot?.querySelector(
            '#shopify-installments-cta'
          );

        cta?.addEventListener(
          'click',
          () => this.#themeDrawer?.close(),
          { once: true }
        );
      });
  };

  #handleDrawerClose = () => {
    this.#unlockPage();
  };

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null
    );

    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      this.#lockPage();
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => {
            /*
             * Horizon replaces the cart drawer markup after a cart update.
             * Wait one additional frame so the new empty-cart state exists.
             */
            requestAnimationFrame(() => {
              if (this.#isCartEmpty()) {
                this.#themeDrawer?.close();
                return;
              }

              this.#updateStickyState();
              this.#updateContinueShoppingLink();
              this.#updateDiscountBadges();
            });
          });

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          this.#lockPage();

          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  #isCartEmpty() {
    return Boolean(
      this.querySelector('.cart-drawer--empty')
    );
  }

  #updateStickyState() {
    const dialog = this.#dialog;

    if (!dialog) return;

    const content = dialog.querySelector(
      '.cart-drawer__content'
    );

    const summary = dialog.querySelector(
      '.cart-drawer__summary'
    );

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    dialog.setAttribute(
      'cart-summary-sticky',
      summaryHeight / drawerHeight >
        this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (
  !customElements.get('cart-drawer-component')
) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}